import { ProCard } from '@ant-design/pro-components'
import { sync } from '@app/icare/client/sync'
import {
  type CostCarrier,
  type CostCarrierIdType,
  CostCarrierSchema,
} from '@app/icare/entities/costCarrier/schema/CostCarrier'
import { EntityDrawerShell } from '@app/icare/form/EntityDrawerShell'
import { AddressFieldGroup } from '@app/icare/form/group/AddressFieldGroup'
import { useEntityDrawer } from '@app/icare/hooks/useEntityDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionDef } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult } from '@pkg/ui/modal'
import { Flex } from 'antd'

const sections = [
  { name: 'general', title: t`Allgemein` },
  { name: 'contact', title: t`Kontakt` },
  { name: 'comment', title: t`Sonstiges` },
] as const satisfies readonly SectionDef[]

type CostCarrierSection = (typeof sections)[number]['name']

type CostCarrierEditDrawerProps =
  | (EditModalProps<CostCarrier, CostCarrierSection, CostCarrierIdType> & { showGlobalImpactWarning?: boolean })
  | undefined

type CostCarrierEditDrawerShow = (props?: CostCarrierEditDrawerProps) => Promise<EditModalResult<CostCarrier>>

export const CostCarrierEditDrawer = createTypedModal<
  CostCarrierEditDrawerProps,
  EditModalResult<CostCarrier>,
  CostCarrierEditDrawerShow
>(props => {
  const drawer = useEntityDrawer({
    schema: CostCarrierSchema,
    entityId: props?.id,
    sections,
    section: props?.section,
    formDefaults: {
      ...zodDefaultInstance(CostCarrierSchema),
      ...props?.defaults,
    },
    onPersist: async value => {
      await sync.mutate(value)
    },
  })

  const { form } = drawer

  return (
    <EntityDrawerShell drawer={drawer} showWarnings={props?.showGlobalImpactWarning}>
      <SectionFlow.Sections
        items={[
          {
            name: 'general',
            title: t`Allgemein`,
            children: (
              <Flex vertical gap="small">
                <form.AppField
                  name="name"
                  children={field => (
                    <field.Text label={t`Name`} className="grow" disabled={props?.showGlobalImpactWarning} />
                  )}
                />

                <AddressFieldGroup form={form} fields="address" />

                <Flex gap={32}>
                  <form.AppField name="ikNumber.main.ikNumber" children={field => <field.Text label="IK-Nummer" />} />

                  <form.AppField name="addition" children={field => <field.Text label="Zusatz" />} />
                </Flex>
              </Flex>
            ),
          },
          {
            name: 'contact',
            title: t`Kontakt`,
            children: (
              <Flex vertical gap="small">
                <form.AppField name="contact" children={field => <field.ContactGroup hidePreferredCommunication />} />
              </Flex>
            ),
          },
          {
            name: 'comment',
            title: t`Sonstiges`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="comment"
                    children={field => <field.TextArea label={t`Bemerkungen`} className="grow" />}
                  />
                </Flex>
              </ProCard>
            ),
          },
        ]}
      />
    </EntityDrawerShell>
  )
})
